import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import { createPortal } from "react-dom"
import AnalyzeOrderMap from "@/components/maps/AnalyzeOrderMap"
import { Button } from "../ui/button"
import { FilePlus2, FileText, Timer } from "lucide-react"
import AnalyzeOrderVerify from "@/components/maps/AnalyzeOrderVerify"
import { useAnalyzeOrderContext } from "@/contexts/AnalyzeOrderContext"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { toast } from "sonner"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromUser } from "@/jidUtils"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { Textarea } from "../ui/textarea"
import html2canvas from "html2canvas"
import { useTranslation } from "react-i18next"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  ANY_FORMAT_ORDERS_FLAG,
  getOrderFileAccept,
  isSupportedOrderFile,
  unsupportedOrderFileMessage,
} from "@/lib/orderFileSupport"

type AnalyzeOrderButtonProps = {
  className?: string
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  showButton?: boolean
  disabled?: boolean
}

// Helper function to convert text to image file
const convertTextToImage = async (text: string): Promise<File> => {
  // Create a temporary div element with the text
  const tempDiv = document.createElement("div")
  tempDiv.style.position = "absolute"
  tempDiv.style.left = "-9999px"
  tempDiv.style.top = "-9999px"
  tempDiv.style.width = "800px"
  tempDiv.style.padding = "20px"
  tempDiv.style.fontSize = "16px"
  tempDiv.style.fontFamily = "Arial, sans-serif"
  tempDiv.style.lineHeight = "1.5"
  tempDiv.style.color = "#000"
  tempDiv.style.backgroundColor = "#fff"
  tempDiv.style.border = "none"
  tempDiv.style.whiteSpace = "pre-wrap"
  tempDiv.style.wordWrap = "break-word"
  tempDiv.textContent = text

  // Add to document temporarily
  document.body.appendChild(tempDiv)

  try {
    // Convert to canvas
    const canvas = await html2canvas(tempDiv, {
      backgroundColor: "#ffffff",
      scale: 2, // Higher quality
      useCORS: true,
    })

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), "image/png")
    })

    // Create File object
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
    const file = new File([blob], `text-order-${timestamp}.png`, {
      type: "image/png",
    })

    return file
  } finally {
    // Clean up
    document.body.removeChild(tempDiv)
  }
}

const AnalyzeOrder = ({
  onSend,
  showButton = false,
  disabled = false,
}: AnalyzeOrderButtonProps) => {
  const { t } = useTranslation()
  const {
    isVerifyOpen,
    routeJid,
    isMapOpen,
    isRouteFetched,
    files,
    handleResetMessageGenerated,
    isRetrying,
    isProcessing,
    isRouteDetected,
    routeMetaData,
    stops,
    isFailed,
    isFetchingPolylines,
    isFetchingPolylinesFailed,
    polylineErrors,
    clearPolylineErrors,
    handleCloseMap,
    retryProcessing,
    handleOnEdit,
    handleContinue,
    handleFileUpload,
    handleCreateStandstillOrder,
    messageId,
  } = useAnalyzeOrderContext()

  // "Zlecenie postojowe" is only useful when Marcos order creation is
  // actually available for this vhost — same gate NaviOrderPanel uses.
  const { myJid } = useContext(XmppContext)
  let vhost = ""
  try {
    vhost = extractVhostFromUser(myJid?.toString())
  } catch (e) {
    console.error(e)
  }
  const { configured: naviConfigured } = useNaviConfig(vhost)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isTextOrderDialogOpen, setIsTextOrderDialogOpen] = useState(false)
  const [textOrderContent, setTextOrderContent] = useState("")
  const [isProcessingTextOrder, setIsProcessingTextOrder] = useState(false)
  const anyFormatEnabled = Boolean(
    useFeatureFlagEnabled(ANY_FORMAT_ORDERS_FLAG),
  )

  // Trigger file input
  const handleAnalyzeOrder = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Handle file selection
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (files) {
        // Validate file types
        const invalidFiles: string[] = []

        Array.from(files).forEach((file) => {
          if (!isSupportedOrderFile(file, anyFormatEnabled)) {
            invalidFiles.push(file.name)
          }
        })

        if (invalidFiles.length > 0) {
          toast.warning(unsupportedOrderFileMessage(anyFormatEnabled))
          event.target.value = "" // Reset input
          return
        }

        handleFileUpload(files, fileInputRef.current, "file")
      }
      // Reset input to allow selecting same file again
      event.target.value = ""
    },
    [handleFileUpload, anyFormatEnabled],
  )

  // Handle opening text order dialog
  const handleOpenTextOrder = useCallback(() => {
    setIsTextOrderDialogOpen(true)
  }, [])

  // Handle closing text order dialog
  const handleCloseTextOrder = useCallback(() => {
    setIsTextOrderDialogOpen(false)
    setTextOrderContent("")
    setIsProcessingTextOrder(false)
  }, [])

  // Handle dialog open/close changes (for ESC, clicking outside, etc.)
  const handleDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Clear content when closing dialog by any method
      setTextOrderContent("")
      setIsProcessingTextOrder(false)
    }
    setIsTextOrderDialogOpen(open)
  }, [])

  // Handle submitting text order
  const handleSubmitTextOrder = useCallback(async () => {
    if (!textOrderContent.trim()) {
      toast.warning(t("orders.analyzeOrder.enterOrderText"))
      return
    }

    setIsProcessingTextOrder(true)
    try {
      const text = textOrderContent.trim()
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
      // With any-format-orders the backend accepts plain text directly;
      // otherwise fall back to rasterizing the text to a PNG.
      const orderFile = anyFormatEnabled
        ? new File([text], `text-order-${timestamp}.txt`, {
            type: "text/plain",
          })
        : await convertTextToImage(text)

      // Create a FileList-like object
      const fileList = [orderFile] as any
      fileList.item = (index: number) => (index === 0 ? orderFile : null)
      fileList.length = 1

      // Use existing file upload handler with text source
      handleFileUpload(fileList, null, "text", text)

      // Close dialog
      handleCloseTextOrder()
    } catch (error) {
      console.error("Error processing text order:", error)
      toast.error(t("orders.analyzeOrder.textOrderFailed"))
      setIsProcessingTextOrder(false)
    }
  }, [
    textOrderContent,
    handleFileUpload,
    handleCloseTextOrder,
    anyFormatEnabled,
    t,
  ])

  const { activeTab } = useAppSelector(selectTabs)
  useEffect(() => {
    if (activeTab !== TAB_TYPES.CHATS && (isVerifyOpen || isMapOpen)) {
      handleCloseMap()
    }
  }, [activeTab, handleCloseMap, isMapOpen, isVerifyOpen])

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={getOrderFileAccept(anyFormatEnabled)}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {showButton && (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAnalyzeOrder}
            disabled={disabled}
          >
            <FilePlus2 /> {t("orders.analyzeOrder.addTransportOrder")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleOpenTextOrder}
            disabled={disabled}
          >
            <FileText /> {t("orders.analyzeOrder.addTextOrder")}
          </Button>
          {naviConfigured && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCreateStandstillOrder}
              disabled={disabled}
            >
              <Timer /> {t("orders.analyzeOrder.standstillOrder")}
            </Button>
          )}
        </div>
      )}

      {routeJid &&
        isVerifyOpen &&
        createPortal(
          <AnalyzeOrderVerify
            onCloseFullScreen={handleCloseMap}
            onEdited={handleOnEdit}
            onResetMessageGenerated={handleResetMessageGenerated}
            onRetryProcessing={retryProcessing}
            onContinue={handleContinue}
            files={files}
            stops={stops}
            routeMetaData={routeMetaData}
            isLoading={isProcessing && !isRouteDetected}
            isFailed={isFailed}
            isRetrying={isRetrying}
            isFetchingPolylines={isFetchingPolylines}
            isFetchingPolylinesFailed={isFetchingPolylinesFailed}
            polylineErrors={polylineErrors}
            onClearPolylineErrors={clearPolylineErrors}
            isRouteFetched={isRouteFetched}
            messageId={messageId}
          />,
          document.body,
        )}

      {routeJid &&
        isMapOpen &&
        createPortal(<AnalyzeOrderMap onSend={onSend} />, document.body)}

      <Dialog
        open={isTextOrderDialogOpen}
        onOpenChange={handleDialogOpenChange}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("orders.analyzeOrder.textOrderDialog.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("orders.analyzeOrder.textOrderDialog.description")}
            </p>
            <Textarea
              placeholder={t("orders.analyzeOrder.textOrderDialog.placeholder")}
              value={textOrderContent}
              onChange={(e) => setTextOrderContent(e.target.value)}
              rows={15}
              className="min-h-[400px] text-base"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseTextOrder}
              disabled={isProcessingTextOrder}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="ghost"
              className="bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
              onClick={handleSubmitTextOrder}
              disabled={isProcessingTextOrder || !textOrderContent.trim()}
            >
              {isProcessingTextOrder
                ? t("orders.analyzeOrder.textOrderDialog.processing")
                : t("orders.analyzeOrder.textOrderDialog.analyze")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AnalyzeOrder
